// 🔴 CHANTIER — RELEVÉ DE TRAVAUX (parcours V1), sur le moteur `chantier/` SEUL.
//
//   Nouvelle visite → dictée → POST /postes → PROPOSITION affichée
//   → confirmation HUMAINE → POST /postes/confirmer → poste persisté → poste suivant
//
// 🔑 UNE PROPOSITION N'EST PAS UN POSTE. Elle s'affiche à part, marquée « pas
//    encore enregistrée » ; la liste des postes ⛔ ne bouge qu'avec la réponse de
//    `/postes/confirmer` (la seule route qui persiste).
// ⛔ Aucun prix, ⛔ aucune TVA : aucun champ, aucun affichage (le serveur les
//    retire déjà ; l'écran retire en plus les mentions de prix du motif).
// ⛔ Aucune unité inventée : `null` s'affiche « sans unité » et repart `null`.
// ⛔ Aucune sélection silencieuse : sur une ambiguïté, rien n'est présélectionné et
//    « Confirmer » reste inactif tant que l'humain n'a pas choisi.
// ⛔ Frontière : ⛔ `/api/visite/*`, ⛔ composant DAL, ⛔ `MobileChantierPage`
//    (garde_frontiere_chantier.test.js).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { OHGrid } from "../../components/ui/oh-grid";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { apiBureau } from "./api";
import { useDictee } from "./useDictee";
import { Encart, NoteVisite, PhotosNonEnvoyees, PhotosReleve, TerminerVisite, RelevesDuDossier, ZonePhotosAvantDevis } from "./AnnexesReleve";

const nombre = (v) => (v === null || v === undefined || v === ""
  ? "" : Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 4 }));

/** ⛔ Le terrain ne lit aucune mention de prix : on retire ces phrases du motif. */
export function motifSansPrix(motif) {
  return String(motif || "")
    .split(/(?<=[.!?])\s+/)
    .filter((phrase) => !/prix|tva|€/i.test(phrase))
    .join(" ")
    .trim();
}

/** Le manque « prix » concerne le bureau, ⛔ le terrain. */
export const manquesTerrain = (manques) => (manques || []).filter((m) => !/prix|tva/i.test(m));

const texteUnite = (u) => (u ? u : "sans unité");

/** 🔴 N°915-B — CE QUI MANQUE, NOMMÉ. Le Core autorise DÉLIBÉRÉMENT un poste sans
 *  quantité (`terminer_releve` : « ⛔ AUCUNE exigence de complétude… Exiger ici
 *  serait décider à la place du métier »). ⇒ On ⛔ n'INTERDIT pas : on refuse que ce
 *  soit SILENCIEUX. Mario a confirmé des postes vides sans savoir qu'ils l'étaient
 *  (N°914-B). Fonction pure ⇒ le banc l'éprouve sans monter l'écran. */
export function manqueAvantConfirmation(quantite, unite) {
  const m = [];
  if (!quantiteValide(quantite)) m.push("quantité");
  if (!unite) m.push("unité");
  return m;
}

/** 🔴 MARIO V1 (décision CTO 02/10) — la QUANTITÉ est OBLIGATOIRE : un nombre > 0.
 *  ⚠️ Le SERVEUR est l'autorité (422 sinon) ; l'écran s'y aligne pour ne pas proposer
 *  un geste qui serait refusé. */
export function quantiteValide(quantite) {
  if (quantite === "" || quantite === null || quantite === undefined) return false;
  const n = Number(String(quantite).replace(",", "."));
  return Number.isFinite(n) && n > 0;
}

function Dimensions({ dims, testId }) {
  if (!dims || !dims.length) return null;
  return (
    <ul data-testid={testId} className="text-sm" style={{ color: "var(--oh-text-secondary)", lineHeight: 1.6 }}>
      {dims.map((d, i) => {
        const facteurs = d.facteurs || d.valeurs || [];
        const unites = d.unites_facteurs || [];
        const calcul = facteurs.map((f, k) => `${nombre(f)}${unites[k] || d.unite_source ? ` ${unites[k] || d.unite_source}` : ""}`).join(" × ");
        return (
          <li key={i}>
            {calcul}
            {d.resultat !== undefined && d.resultat !== null && (
              <> = <span data-testid={`${testId}-resultat`}>{nombre(d.resultat)} {d.unite_resultat || ""}</span>
                {" "}(surface calculée)</>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ListePostes({ postes, motPoste }) {
  if (!postes?.length) {
    return <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Aucun {motPoste.toLowerCase()} enregistré.</p>;
  }
  return (
    <ol data-testid="postes-enregistres" style={{ display: "grid", gap: 8 }}>
      {postes.map((p) => (
        <li key={p.id} data-testid="poste-enregistre"
            style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
                     borderRadius: 12, padding: 12, color: "var(--oh-text)" }}>
          <div style={{ fontWeight: 500 }}>{p.designation}</div>
          <div className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
            {p.quantite !== null && p.quantite !== undefined ? nombre(p.quantite) : "quantité non renseignée"}
            {" · "}{texteUnite(p.unite)}
          </div>
          <Dimensions dims={p.dimensions} testId={`poste-dimensions-${p.id}`} />
        </li>
      ))}
    </ol>
  );
}

/** 🔴 P-MARIO-04 — les postes du relevé EN COURS, dans la grille partagée `OHGrid` :
 *  sélection de lignes, « Supprimer » par ligne, suppression de masse, TOUJOURS après
 *  une confirmation explicite (`alert-dialog`, ⛔ `window.confirm`). La liste affichée
 *  est celle que RENVOIE le serveur après suppression (⛔ retrait local optimiste).
 *  Ordre = ordre de saisie (clé `rang`, provisoire). ⛔ Relevé clos : `ListePostes`. */
export function GrillePostes({ postes, motPoste, envoi, onRetirer, onCorriger, unitesAutorisees, chargerProduits }) {
  const [demande, setDemande] = useState(null);   // { ids, designations, masse }
  // 🔴 N°915-C — correction d'un poste DÉJÀ enregistré : { id, designation, quantite, unite }.
  const [correction, setCorrection] = useState(null);
  // 🔴 N°1016 — référentiel du tenant, chargé À L'OUVERTURE de la correction (⛔ jamais avant) :
  //    permet d'affecter une référence à un poste libre. Indisponible ⇒ le sélecteur n'apparaît pas.
  const [produits, setProduits] = useState(null);
  const idCorrection = correction?.id;
  useEffect(() => {
    if (!idCorrection || !chargerProduits) return undefined;
    let vivant = true;
    chargerProduits().then((p) => vivant && setProduits(p)).catch(() => vivant && setProduits([]));
    return () => { vivant = false; };
  }, [idCorrection]); // eslint-disable-line react-hooks/exhaustive-deps -- le chargeur est recréé à chaque rendu parent
  const mot = motPoste.toLowerCase();
  if (!postes?.length) return <ListePostes postes={postes} motPoste={motPoste} />;

  const lignes = postes.map((p, i) => ({ ...p, rang: String(i + 1).padStart(4, "0") }));
  const demander = (ids, masse) => setDemande({
    ids, masse, designations: lignes.filter((l) => ids.includes(l.id)).map((l) => l.designation),
  });
  const n = demande?.ids.length || 0;
  const colonnes = [
    { key: "designation", label: "Désignation",
      render: (v, p) => (<div><div style={{ fontWeight: 500 }}>{v}</div>
        <Dimensions dims={p.dimensions} testId={`poste-dimensions-${p.id}`} /></div>) },
    { key: "quantite", label: "Quantité", sortable: false,
      render: (v) => (v !== null && v !== undefined ? nombre(v) : "non renseignée") },
    { key: "unite", label: "Unité", render: (v) => texteUnite(v) },
    { key: "nombre_medias", label: "Photos", sortable: false, render: (v) => v || 0 },
    { key: "rang", label: "N°", render: (v) => Number(v) },
  ];
  return (
    <section data-testid="postes-grille-section" aria-label={`${motPoste}(s) enregistré(s)`}>
      <OHGrid data={lignes} columns={colonnes} testIdPrefix="postes-grille"
              defaultSortKey="rang" defaultSortDir="asc"
              searchPlaceholder={`Rechercher un ${mot}…`} emptyMessage={`Aucun ${mot} enregistré.`}
              selectable
              rowActions={[
                // 🔴 N°915-C — un poste confirmé sans quantité était DÉFINITIVEMENT en
                //    lecture seule dans cet écran (N°914-B) : la seule issue était de le
                //    SUPPRIMER et de tout redicter. ⛔ Aucune nouvelle route : `PATCH
                //    …/postes/{id}` existe déjà (relevé clos refusé, unité contrôlée,
                //    tenant scopé). L'action ⛔ n'apparaît QUE si le geste est fourni —
                //    le relevé clos rend `ListePostes`, ⛔ pas cette grille.
                ...(onCorriger ? [{ label: "Corriger", icon: Pencil,
                  onClick: (row) => setCorrection({
                    id: row.id, designation: row.designation,
                    quantite: row.quantite === null || row.quantite === undefined ? "" : String(row.quantite),
                    unite: row.unite || "", reference: row.reference_referentiel || "" }) }] : []),
                { label: "Supprimer", icon: Trash2, onClick: (row) => demander([row.id], false) }]}
              bulkActions={[{ label: (k) => `Supprimer (${k})`, icon: Trash2, onClick: (ids) => demander(ids, true) }]} />
      <AlertDialog open={demande !== null} onOpenChange={(ouvert) => { if (!ouvert) setDemande(null); }}>
        <AlertDialogContent data-testid="suppression-dialogue"
                            style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text)",
                                     borderColor: "var(--oh-border)", borderRadius: 12 }}>
          <AlertDialogHeader>
            <AlertDialogTitle>{n > 1 ? `Supprimer ${n} ${mot}(s) ?` : `Supprimer ce ${mot} ?`}</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--oh-text-secondary)" }}>
              {demande?.designations.join(" · ")}. Les photos rattachées restent dans la visite, sans {mot}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="annuler-suppression"
                               style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction data-testid="confirmer-suppression" disabled={envoi}
                               onClick={() => onRetirer(demande.ids, demande.masse)}
                               style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
              {n > 1 ? `Supprimer (${n})` : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 🔴 N°915-C — CORRIGER quantité et unité. ⛔ On n'envoie QUE ces deux champs :
          le serveur applique `exclude_unset`, donc la désignation, le commentaire et
          l'emplacement sont CONSERVÉS. Un champ vidé part `null` = EFFACÉ (voulu). */}
      <AlertDialog open={correction !== null} onOpenChange={(ouvert) => { if (!ouvert) setCorrection(null); }}>
        <AlertDialogContent data-testid="correction-dialogue"
                            style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text)",
                                     borderColor: "var(--oh-border)", borderRadius: 12 }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Corriger le poste</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--oh-text-secondary)" }}>
              {correction?.designation} — la désignation, le commentaire et les photos ⛔ ne changent pas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <label className="text-sm" style={{ color: "var(--oh-text)", display: "grid", gap: 4 }}>
              Quantité
              <input data-testid="correction-quantite" inputMode="decimal" placeholder="non renseignée"
                     value={correction?.quantite ?? ""}
                     onChange={(ev) => setCorrection((c) => ({ ...c, quantite: ev.target.value }))}
                     style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)",
                              padding: "0 8px", background: "var(--oh-bg)", color: "var(--oh-text)" }} />
            </label>
            <label className="text-sm" style={{ color: "var(--oh-text)", display: "grid", gap: 4 }}>
              Unité
              <select data-testid="correction-unite" value={correction?.unite ?? ""}
                      onChange={(ev) => setCorrection((c) => ({ ...c, unite: ev.target.value }))}
                      style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)",
                               padding: "0 8px", background: "var(--oh-bg)", color: "var(--oh-text)" }}>
                <option value="">sans unité</option>
                {/* ⛔ Aucune unité inventée : la liste est celle que le PROFIL autorise —
                    la même que la saisie et la proposition. L'unité portée par le poste y
                    est ajoutée pour ⛔ ne pas la faire disparaître à l'ouverture. */}
                {[...new Set([...(unitesAutorisees || []), ...(correction?.unite ? [correction.unite] : [])])]
                  .map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
          </div>
          {chargerProduits && produits && produits.length > 0 && (
            <label className="text-sm" style={{ color: "var(--oh-text)", display: "grid", gap: 4 }}>
              Référence catalogue
              <select data-testid="correction-reference" value={correction?.reference ?? ""}
                      onChange={(ev) => {
                        const ref = ev.target.value;
                        const produit = produits.find((x) => x.reference === ref);
                        // 🔑 L'unité d'un produit connu vient de lui (le serveur l'impose de toute façon).
                        setCorrection((c) => ({ ...c, reference: ref, unite: produit?.unite || c.unite }));
                      }}
                      style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)",
                               padding: "0 8px", background: "var(--oh-bg)", color: "var(--oh-text)" }}>
                <option value="">aucune référence</option>
                {produits.map((x) => (
                  <option key={x.reference} value={x.reference}>{x.designation} ({x.reference})</option>
                ))}
              </select>
              {!correction?.reference && (
                <span style={{ color: "var(--oh-text-secondary)" }}>
                  Sans référence catalogue, ce poste ne peut pas être chiffré dans le devis.
                </span>
              )}
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="annuler-correction"
                               style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction data-testid="valider-correction" disabled={envoi}
                               onClick={() => onCorriger(correction.id, {
                                 quantite: correction.quantite === ""
                                   ? null : Number(String(correction.quantite).replace(",", ".")),
                                 unite: correction.unite || null,
                                 // ⚠️ La référence ne part QUE si elle a changé : un champ absent est CONSERVÉ.
                                 ...((correction.reference || "") !== (lignes.find((l) => l.id === correction.id)?.reference_referentiel || "")
                                   ? { reference_referentiel: correction.reference || null } : {}) })}
                               style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
              Enregistrer la correction
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

/** É8 — qui a ouvert le relevé en cours, et quand (données déjà servies par l'API). */
function AuteurReleve({ releve, vocab }) {
  const quand = releve.ouvert_le ? new Date(releve.ouvert_le) : null;
  const date = quand && !Number.isNaN(quand.getTime())
    ? `${quand.toLocaleDateString("fr-FR")} à ${quand.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`
    : "date inconnue";
  return (
    <p data-testid="releve-auteur" className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
      {vocab.session || "Visite"} ouverte le {date} par {releve.operateur || "auteur non renseigné"}
    </p>
  );
}

/** La PROPOSITION du moteur : relue, corrigée, puis CONFIRMÉE par l'humain. */
// 🔴 N°1026 — ÉCRAN MOBILE : seules les meilleures correspondances sont PRÉSENTÉES (le moteur cherche toujours dans tout le
//    catalogue). Règle GÉNÉRIQUE, ⛔ aucune exception de métier : on garde le petit groupe dont le score est proche du
//    meilleur (≥ 60 % de celui-ci, 5 au plus) ; les autres restent accessibles via « Autres choix ».
const RATIO_PROCHE = 0.6;
const PRINCIPAUX_MAX = 5;
const MOTS_LIAISON = new Set(["a", "au", "aux", "de", "du", "des", "d", "la", "le", "les", "l", "en", "et", "sur", "pour"]);

export function separerCandidats(candidats) {
  const liste = candidats || [];
  if (!liste.length || liste.some((c) => typeof c.score !== "number")) return { principaux: liste, autres: [] };
  const tries = [...liste].sort((a, b) => b.score - a.score);
  const seuil = tries[0].score * RATIO_PROCHE;
  const principaux = tries.filter((c) => c.score >= seuil).slice(0, PRINCIPAUX_MAX);
  const ids = new Set(principaux.map((c) => c.reference));
  return { principaux, autres: tries.filter((c) => !ids.has(c.reference)) };
}

const mots = (t) => String(t || "").trim().split(/\s+/).filter(Boolean);
const nu = (m) => m.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");

/** Libellés COURTS : on retire le début commun à tous les choix proposés (« Mise en peinture de la porte cochère de
 *  l'immeuble » + « face intérieure » / « face extérieure » / « aux deux faces »). Aucun libellé ne doit devenir vide. */
export function libellesCourts(candidats) {
  const pleins = (candidats || []).map((c) => c.designation || "");
  if (pleins.length < 2) return pleins;
  const m = pleins.map(mots);
  let n = 0;
  while (m.every((x) => x.length > n && nu(x[n]) === nu(m[0][n]))) n += 1;
  if (n === 0) return pleins;
  const courts = m.map((x) => {
    let reste = x.slice(n);
    while (reste.length > 1 && MOTS_LIAISON.has(nu(reste[0]))) reste = reste.slice(1);
    const t = reste.join(" ");
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
  });
  return courts.some((t) => !t) || new Set(courts.map(nu)).size !== courts.length ? pleins : courts;
}

export function Proposition({ proposition, unitesAutorisees, motPoste, onConfirmer, onAbandonner, envoi,
                      onDicterQuantite, compact = false }) {
  const [designation, setDesignation] = useState(proposition.designation || "");
  const [quantite, setQuantite] = useState(
    proposition.quantite === null || proposition.quantite === undefined ? "" : String(proposition.quantite));
  // 🔴 MARIO V1 — l'unité d'un produit CONNU est reportée (⛔ ressaisie inutile) ; le
  //    serveur la pose de toute façon depuis le référentiel.
  const [unite, setUnite] = useState(proposition.unite || proposition.produit_retenu?.unite || "");
  const [choix, setChoix] = useState(null);   // ⛔ aucune présélection
  // 🔴 N°919 — ÉTAPE 2 : la quantité se DICTE, dans son propre temps.
  const [quantiteDictee, setQuantiteDictee] = useState("");
  const [litQuantite, setLitQuantite] = useState(false);
  const [quantiteIncomprise, setQuantiteIncomprise] = useState(false);
  // 🔴 P1 DICTÉE DES SURFACES — dimensions d'unité NON dite (« 310 × 0,7 ») : le serveur ne calcule RIEN et
  //    PROPOSE des lectures ; elles ne sont appliquées qu'au CLIC de l'humain (⛔ jamais présélectionnées).
  const [confirmationUnite, setConfirmationUnite] = useState(proposition.confirmation || null);
  // T-211 · 24 — le prix DIT par la personne : montré, MODIFIABLE, jamais deviné. Vide = pas de prix dicté (le catalogue fait foi).
  const prixDit = proposition.prix_dicte || null;
  const [prixMontant, setPrixMontant] = useState(prixDit && prixDit.montant != null ? String(prixDit.montant).replace(".", ",") : "");
  const [prixPar, setPrixPar] = useState(prixDit && prixDit.par ? prixDit.par : "");
  // 🔴 N°1027 — l'unité d'une prestation du catalogue est celle du CATALOGUE (règle serveur : un produit connu se compte dans
  //    SON unité). Si elle diffère de l'unité dictée (« 25 m² » pour une prestation à l'unité), l'écran le DIT au lieu de
  //    remplacer l'unité en silence.
  const [uniteLue, setUniteLue] = useState(null);
  const [conflitUnite, setConflitUnite] = useState(null);
  /** Fait LIRE l'énoncé par le serveur (⛔ pas d'interprétation côté téléphone :
   *  ce serait une seconde implémentation du cœur). Remplit les champs de contrôle. */
  const lireTexte = async (texte) => {
    if (!String(texte || "").trim() || !onDicterQuantite) return;
    setLitQuantite(true); setQuantiteIncomprise(false); setConfirmationUnite(null);
    try {
      const lu = await onDicterQuantite(texte);
      if (lu && lu.confirmation) { setConfirmationUnite(lu.confirmation); setQuantiteDictee(""); return; }
      if (lu && lu.quantite !== null && lu.quantite !== undefined) {
        setQuantite(String(lu.quantite));
        // ⛔ Une unité non DITE ⛔ n'écrase pas celle déjà proposée (catalogue).
        if (lu.unite) {
          setUniteLue(lu.unite);
          const retenu = choix && choix !== "__aucune__" ? candidats.find((c) => c.reference === choix) : null;
          if (retenu?.unite && retenu.unite !== lu.unite) { setConflitUnite({ lue: lu.unite, produit: retenu.unite }); setUnite(retenu.unite); }
          else { setConflitUnite(null); setUnite(lu.unite); }
        }
        setQuantiteDictee("");
      } else {
        setQuantiteIncomprise(true);   // ⛔ aucune valeur posée au hasard
      }
    } finally { setLitQuantite(false); }
  };
  const lireQuantite = () => lireTexte(quantiteDictee);
  const appliquerLecture = (l) => {
    setQuantite(String(l.quantite));
    setUnite(l.unite || "");
    setConfirmationUnite(null);
  };
  // 🔴 N°1026 (mobile) — « 25 mètres carrés » dicté ⇒ quantité 25, unité m² POSÉES DIRECTEMENT : ni second report, ni bouton.
  //    Seul un énoncé NON compris laisse le champ texte et « Reporter » (⛔ rien n'est deviné).
  const { disponible: microQ, ecoute: ecouteQ, demarrer: dicterQ } = useDictee((t) => {
    if (compact && onDicterQuantite) lireTexte(t);
    else setQuantiteDictee((d) => (d ? `${d} ${t}` : t));
  });

  const candidats = proposition.candidats || [];
  const ambigu = !proposition.produit_retenu && candidats.length > 0;
  const { principaux, autres } = compact ? separerCandidats(candidats) : { principaux: candidats, autres: [] };
  const [autresOuverts, setAutresOuverts] = useState(false);
  const courts = compact ? libellesCourts(principaux) : principaux.map((c) => c.designation);
  const unites = [...new Set([...(unitesAutorisees || []), ...(proposition.unite ? [proposition.unite] : []),
                              ...((confirmationUnite?.lectures || []).map((l) => l.unite).filter(Boolean))])];
  const prixSaisi = prixMontant.trim() !== "";
  const prixValide = !prixSaisi || (Number.isFinite(Number(prixMontant.replace(",", "."))) && Number(prixMontant.replace(",", ".")) >= 0 && prixPar !== "");
  const pret = designation.trim() && (!ambigu || choix !== null) && quantiteValide(quantite) && prixValide;
  // 🔴 N°915-B — ⛔ aucun blocage, mais ⛔ aucun silence : ce qui manque est NOMMÉ, et
  //    le bouton le DIT. Un « Confirmer » neutre laissait croire la ligne complète.
  const manquants = manqueAvantConfirmation(quantite, unite);
  const libelleConfirmer = !quantiteValide(quantite)
    ? "Quantité obligatoire"
    : (manquants.length ? `Confirmer sans ${manquants.join(" ni ")}` : `Confirmer ce ${motPoste.toLowerCase()}`);
  const motif = motifSansPrix(proposition.motif);
  const manques = manquesTerrain(proposition.manques);

  const confirmer = () => {
    const corps = {
      designation: designation.trim(),
      quantite: quantite === "" ? null : Number(String(quantite).replace(",", ".")),
      unite: unite || null,                 // ⛔ jamais une unité par défaut
    };
    // N°1030 — dictée AU MICRO incluse : sans le texte entendu, le serveur ne réinterprète pas et ne pose aucune référence.
    if ((proposition.origine === "dictee" || proposition.origine === "dictee_audio") && proposition.dictee) {
      corps.dictee = proposition.dictee;
    }
    if (ambigu && choix && choix !== "__aucune__") corps.reference_referentiel = choix;
    // T-211 · 24 — ce que l'écran montre est ce qui part : prix corrigé, ou retiré (null) si la personne l'a effacé.
    if (prixSaisi) corps.prix_dicte = { montant: Number(prixMontant.replace(",", ".")), par: prixPar };
    else if (prixDit) corps.prix_dicte = null;
    onConfirmer(corps);
  };

  return (
    <section data-testid="proposition" aria-label="Proposition à confirmer"
             style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-accent)",
                      borderRadius: 12, padding: 16, display: "grid", gap: 12 }}>
      {!compact && (
        <Encart ton="info" testId="proposition-statut">
          Étape 2 sur 2 — <strong style={{ fontWeight: 500 }}>pas encore enregistrée</strong>.
          Relisez la désignation, la quantité et l'unité, corrigez si besoin, puis confirmez :
          c'est la confirmation qui enregistre le {motPoste.toLowerCase()}.
        </Encart>
      )}
      {!compact && proposition.dictee && (
        <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
          Entendu : « {proposition.dictee} »
        </p>
      )}
      {proposition.autres_ignores > 0 && (
        <Encart ton="error" testId="autres-ignores">
          {proposition.autres_ignores} autre(s) {motPoste.toLowerCase()}(s) entendu(s) dans cette dictée
          ne sont pas proposé(s) : un appel = un {motPoste.toLowerCase()}. Dictez-les séparément.
        </Encart>
      )}

      <label className="text-sm" style={{ color: "var(--oh-text)", display: "grid", gap: 4 }}>
        Désignation
        <input data-testid="proposition-designation" value={designation}
               onChange={(ev) => setDesignation(ev.target.value)}
               style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)",
                        padding: "0 8px", background: "var(--oh-bg)", color: "var(--oh-text)" }} />
      </label>
      {onDicterQuantite && (
        // ⚠️ testid PRÉFIXÉ `etape-` : `quantite-dictee` tout court est DÉJÀ pris par
        //    l'ANCIEN écran (`MobileChantierPage.jsx:305`). Deux écrans partageant un
        //    testid, c'est un banc qui croit cliquer l'un et clique l'autre.
        <section data-testid="etape-quantite-dictee" style={{ display: "grid", gap: 8 }}>
          {(!compact || !microQ || quantiteIncomprise) && (
          <label className="text-sm" style={{ color: "var(--oh-text)", display: "grid", gap: 4 }}>
            Dictez la quantité
            <input data-testid="quantite-dictee-texte" value={quantiteDictee}
                   placeholder="ex. « 25 mètres carrés », « 3 unités », ou « 25 »"
                   onChange={(ev) => { setQuantiteDictee(ev.target.value); setQuantiteIncomprise(false); }}
                   style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)",
                            padding: "0 8px", background: "var(--oh-bg)", color: "var(--oh-text)" }} />
          </label>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {microQ && (
              <Button variant="outline" data-testid="quantite-dictee-micro" onClick={dicterQ} disabled={ecouteQ || envoi}
                      style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent" }}>
                {ecouteQ ? "J'écoute…" : "Dicter la quantité"}
              </Button>
            )}
            {(!compact || !microQ || quantiteIncomprise) && (
              <Button variant="outline" data-testid="quantite-dictee-lire" onClick={lireQuantite}
                      disabled={!quantiteDictee.trim() || litQuantite || envoi}
                      style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}>
                Reporter dans la quantité
              </Button>
            )}
          </div>
          {quantiteIncomprise && (
            <Encart ton="error" testId="quantite-non-comprise">
              Cet énoncé ⛔ n'a pas pu être lu comme une quantité. ⛔ Rien n'a été
              deviné : redictez-le, ou saisissez la quantité à la main ci-dessous.
            </Encart>
          )}
        </section>
      )}
      {confirmationUnite && (
        <Encart ton="error" testId="confirmation-unite">
          <p>{confirmationUnite.question}</p>
          <p>{(confirmationUnite.lectures || []).length
            ? "⛔ Rien n'a été calculé ni deviné : choisissez la bonne lecture."
            : "⛔ Rien n'a été calculé ni deviné : aucune lecture n'est proposée, redictez la quantité."}</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {(confirmationUnite.lectures || []).map((l, i) => (
              <Button key={i} variant="outline" data-testid={`confirmation-unite-lecture-${i}`}
                      aria-label={`Choisir ${l.libelle}`} onClick={() => appliquerLecture(l)}
                      style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent" }}>
                {l.libelle}
              </Button>
            ))}
          </div>
        </Encart>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <label className="text-sm" style={{ color: "var(--oh-text)", display: "grid", gap: 4 }}>
          Quantité
          <input data-testid="proposition-quantite" inputMode="decimal" value={quantite}
                 placeholder="non renseignée" onChange={(ev) => setQuantite(ev.target.value)}
                 style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)",
                          padding: "0 8px", background: "var(--oh-bg)", color: "var(--oh-text)" }} />
        </label>
        <label className="text-sm" style={{ color: "var(--oh-text)", display: "grid", gap: 4 }}>
          Unité
          <select data-testid="proposition-unite" value={unite} onChange={(ev) => setUnite(ev.target.value)}
                  style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)",
                           padding: "0 8px", background: "var(--oh-bg)", color: "var(--oh-text)" }}>
            <option value="">sans unité</option>
            {unites.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </label>
      </div>
      {(prixSaisi || prixDit) && (
        <fieldset data-testid="prix-dicte" style={{ display: "grid", gap: 8, border: 0, padding: 0 }}>
          <legend className="text-sm" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
            Prix dicté (HT) — modifiable
          </legend>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <label className="text-sm" style={{ color: "var(--oh-text)", display: "grid", gap: 4 }}>
              Prix HT en euros
              <input data-testid="prix-dicte-montant" inputMode="decimal" value={prixMontant}
                     placeholder="aucun prix dicté" onChange={(ev) => setPrixMontant(ev.target.value)}
                     style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)",
                              padding: "0 8px", background: "var(--oh-bg)", color: "var(--oh-text)" }} />
            </label>
            <label className="text-sm" style={{ color: "var(--oh-text)", display: "grid", gap: 4 }}>
              Prix compté
              <select data-testid="prix-dicte-par" value={prixPar} onChange={(ev) => setPrixPar(ev.target.value)}
                      style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)",
                               padding: "0 8px", background: "var(--oh-bg)", color: "var(--oh-text)" }}>
                <option value="">à préciser</option>
                <option value="m²">au m²</option>
                <option value="ml">au mètre linéaire</option>
                <option value="m³">au m³</option>
                <option value="u">à la pièce</option>
                <option value="forfait">forfait</option>
              </select>
            </label>
          </div>
          {prixSaisi && prixPar === "" && (
            <Encart ton="error" testId="prix-dicte-unite-manquante">
              L'unité du prix n'a pas été entendue : choisissez-la. Rien n'est deviné.
            </Encart>
          )}
        </fieldset>
      )}
      {compact && conflitUnite && (
        <p data-testid="unite-catalogue" className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
          Cette prestation se compte en « {conflitUnite.produit} » : le « {conflitUnite.lue} » dicté n'est pas retenu.
        </p>
      )}
      <Dimensions dims={proposition.dimensions} testId="proposition-dimensions" />
      {!compact && manquants.length > 0 && (
        <Encart ton="error" testId="proposition-incomplete">
          {manquants.length > 1 ? "Ni la quantité ni l'unité ne sont renseignées" :
            (manquants[0] === "quantité" ? "La quantité n'est pas renseignée" : "L'unité n'est pas renseignée")}.
          Complétez ci-dessus, ou confirmez sans : le {motPoste.toLowerCase()} sera enregistré
          {manquants.length > 1 ? " sans quantité ni unité" :
            (manquants[0] === "quantité" ? " sans quantité" : " sans unité")} — vous pourrez le corriger ensuite.
        </Encart>
      )}

      {proposition.produit_retenu && (
        <p data-testid="correspondance" className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
          Correspondance : {proposition.produit_retenu.designation}
          {!compact && proposition.produit_retenu.reference ? ` (${proposition.produit_retenu.reference})` : ""}
        </p>
      )}
      {ambigu && (
        <fieldset data-testid="ambiguite" style={{ display: "grid", gap: 4, border: 0, padding: 0 }}>
          <legend className="text-sm" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
            {compact ? "Quelle prestation ?" : "Plusieurs correspondances possibles — choisissez :"}
          </legend>
          {principaux.map((c, i) => (
            <label key={c.reference} className="text-sm" style={{ color: "var(--oh-text)" }}>
              <input type="radio" name="choix-candidat" data-testid={`candidat-${c.reference}`}
                     checked={choix === c.reference} onChange={() => { setChoix(c.reference); if (c.unite) setUnite(c.unite); setConflitUnite(uniteLue && c.unite && c.unite !== uniteLue ? { lue: uniteLue, produit: c.unite } : null); }} />{" "}
              {courts[i] || c.designation}{compact ? "" : ` (${c.reference})`}
            </label>
          ))}
          {compact && autres.length > 0 && !autresOuverts && (
            <Button variant="outline" data-testid="autres-choix" onClick={() => setAutresOuverts(true)}
                    style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent", height: 32 }}>
              Autres choix
            </Button>
          )}
          {compact && autresOuverts && autres.map((c) => (
            <label key={c.reference} className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
              <input type="radio" name="choix-candidat" data-testid={`candidat-${c.reference}`}
                     checked={choix === c.reference} onChange={() => { setChoix(c.reference); if (c.unite) setUnite(c.unite); setConflitUnite(uniteLue && c.unite && c.unite !== uniteLue ? { lue: uniteLue, produit: c.unite } : null); }} />{" "}
              {c.designation}
            </label>
          ))}
          <label className="text-sm" style={{ color: "var(--oh-text)" }}>
            <input type="radio" name="choix-candidat" data-testid="candidat-aucun"
                   checked={choix === "__aucune__"} onChange={() => { setChoix("__aucune__"); setConflitUnite(null); if (uniteLue) setUnite(uniteLue); }} />{" "}
            {compact ? "Garder mon libellé" : "Aucune — garder la désignation libre"}
          </label>
        </fieldset>
      )}
      {!compact && motif && <p data-testid="proposition-motif" className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>{motif}</p>}
      {!compact && manques.length > 0 && (
        <p data-testid="proposition-manques" className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
          À compléter si possible : {manques.join(", ")}.
        </p>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Button data-testid="confirmer-poste" disabled={!pret || envoi} onClick={confirmer}
                style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
          {libelleConfirmer}
        </Button>
        <Button data-testid="abandonner-proposition" variant="outline" onClick={onAbandonner} disabled={envoi}
                style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}>
          Abandonner
        </Button>
      </div>
    </section>
  );
}

// 🔴 N°975 — `actionsReleve(releve)` : actions INJECTÉES par l'hôte sous un relevé consulté
//    (ex. « Créer le devis », qui vit dans `metier/artisan/`). ⛔ Ce module ⛔ ne les importe
//    pas : le relevé reste limité à `/api/chantier/*` (garde_frontiere_artisan, N°877).
const CARTE_MENU = { background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: 12, padding: 12 };

export function ReleveChantier({ dossierId, profilCode, api: apiFourni, ouvrable, onAccesPerdu,
                                 actionsReleve, consulterId, compact = false, menuOuvert = false, finaliser }) {
  // 🔑 UNE interface, deux transports : bureau (session) par défaut, terrain (jeton) fourni.
  const api = useMemo(() => apiFourni || apiBureau(dossierId), [apiFourni, dossierId]);
  const peutOuvrir = ouvrable ?? Boolean(profilCode);
  const [chargement, setChargement] = useState(true);
  const [releve, setReleve] = useState(null);
  const [releves, setReleves] = useState([]);
  const [consultation, setConsultation] = useState(null);
  // 🔴 N°1016 — confirmation d'abandon de la visite en cours.
  const [abandonDemande, setAbandonDemande] = useState(false);
  // 🔴 N°1027 — « Créer le devis » = clôture + création du devis à partir des travaux enregistrés (hôte : `finaliser`).
  const [finalisation, setFinalisation] = useState(false);
  const [erreurFinale, setErreurFinale] = useState("");
  const [devisCree, setDevisCree] = useState(null);
  const [reprise, setReprise] = useState(false);
  const [erreur, setErreur] = useState("");
  const [mode, setMode] = useState("dictee");
  const [dictee, setDictee] = useState("");
  const [saisie, setSaisie] = useState({ designation: "", quantite: "", unite: "" });
  const [proposition, setProposition] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [enregistreLe, setEnregistreLe] = useState(null);
  // 🔴 N°919 — après CHAQUE confirmation, le terrain choisit EXPLICITEMENT :
  //    « Nouveau poste » ou « Terminer la visite ». ⛔ Plus de retour implicite à
  //    la dictée : Mario ⛔ ne savait pas où il en était dans le cycle.
  const [choixApres, setChoixApres] = useState(false);

  const ajouterTexte = useCallback((t) => setDictee((d) => (d ? `${d} ${t}` : t)), []);
  // 🔴 N°963 — chemin PRINCIPAL : l'audio du micro est transcrit CÔTÉ SERVEUR, puis le
  //    TEXTE entendu remplit la zone éditable ET la proposition s'affiche (même rendu
  //    que la dictée texte). ⛔ Ne dépend plus de SpeechRecognition du navigateur.
  const dicterParAudio = useCallback(async (blob, dureeS) => {
    const r = await api.dicterAudio(releve.id, blob, dureeS);
    if (r?.proposition) {
      setDictee(r.proposition.transcription || "");
      setProposition(r.proposition);
    }
  }, [api, releve]);
  const { disponible: micro, ecoute, erreur: erreurMicro, demarrer,
          audioDispo, enregistre, occupe: transcription, demarrerAudio, arreterAudio } =
    useDictee(ajouterTexte, { onAudio: dicterParAudio });

  const signaler = useCallback((err) => {
    // ⛔ Jamais un écran mort sur un accès perdu : l'écran terrain reprend la main.
    if (err?.statut === 401 && onAccesPerdu) onAccesPerdu(err); else setErreur(err.message);
  }, [onAccesPerdu]);
  const rafraichirListe = useCallback(() => api.releves().then(setReleves).catch(() => {}), [api]);

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    Promise.all([api.releveEnCours(), api.releves().catch(() => [])])
      // 🔑 « reprise » = il y a DÉJÀ du travail enregistré à conserver. Un relevé vide qu'on
      //    vient d'ouvrir (entrée de l'application, P-MARIO-03) ⛔ n'est pas une reprise.
      .then(([r, liste]) => { if (vivant) { setReleve(r); setReprise(Boolean(r?.postes?.length)); setReleves(liste || []); } })
      .catch((err) => vivant && signaler(err))
      .finally(() => vivant && setChargement(false));
    return () => { vivant = false; };
  }, [api, signaler]);

  // 🔴 N°1016 — l'hôte (liste des visites de la fiche dossier) peut demander l'OUVERTURE EN
  //    CONSULTATION d'un relevé précis. Lecture seule : ⛔ aucune écriture.
  useEffect(() => {
    if (!consulterId || chargement) return undefined;
    let vivant = true;
    api.lire(consulterId).then((r) => vivant && setConsultation(r)).catch((err) => vivant && signaler(err));
    return () => { vivant = false; };
  }, [consulterId, chargement]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔑 Brouillon de dictée : un texte dicté mais pas encore proposé ⛔ se perd pas au rechargement.
  const cleBrouillon = releve?.id ? `releve-brouillon:${releve.id}` : null;
  useEffect(() => {
    if (!cleBrouillon) return;
    try { const b = localStorage.getItem(cleBrouillon); if (b) setDictee((d) => d || b); } catch { /* indisponible */ }
  }, [cleBrouillon]);
  useEffect(() => {
    if (!cleBrouillon) return;
    try { if (dictee) localStorage.setItem(cleBrouillon, dictee); else localStorage.removeItem(cleBrouillon); } catch { /* indisponible */ }
  }, [cleBrouillon, dictee]);

  const garder = async (f) => {
    setErreur(""); setEnvoi(true);
    try { await f(); } catch (err) { signaler(err); } finally { setEnvoi(false); }
  };

  const nouvelleVisite = () => garder(async () => {
    const r = await api.ouvrir(profilCode);
    setReleve(r); setReprise(Boolean(r.reprise)); setConsultation(null);
    rafraichirListe();
  });

  const proposer = () => garder(async () => {
    const corps = mode === "dictee"
      ? { dictee: dictee.trim() }
      : { designation: saisie.designation.trim(),
          quantite: saisie.quantite === "" ? null : Number(String(saisie.quantite).replace(",", ".")),
          unite: saisie.unite || null };
    const r = await api.proposer(releve.id, corps);
    setProposition(r.proposition);        // ⛔ la liste des postes ⛔ ne change PAS ici
  });

  const confirmer = (corps) => garder(async () => {
    const r = await api.confirmer(releve.id, corps);
    setReleve(r);                          // ✅ seule mise à jour de la liste
    setProposition(null);
    setDictee(""); setSaisie({ designation: "", quantite: "", unite: "" });
    setEnregistreLe(new Date().toISOString());
    setChoixApres(true);                   // 🔴 N°919 — le choix, immédiatement
    rafraichirListe();                     // le compteur de la liste suit le relevé (⛔ périmé)
  });

  // 🔴 P-MARIO-04 — suppression CONFIRMÉE : un poste ⇒ DELETE ; une sélection ⇒ UN appel
  //    de masse. La liste affichée est la RÉPONSE du serveur (relevé relu).
  const retirer = (ids, masse) => garder(async () => {
    const r = masse ? await api.retirerPostes(releve.id, ids) : await api.retirer(releve.id, ids[0]);
    setReleve(r);
    setEnregistreLe(new Date().toISOString());
    rafraichirListe();
  });

  // 🔴 N°915-C — CORRIGER un poste enregistré. Même forme que `retirer` : la liste
  //    affichée est la RÉPONSE du serveur (relevé relu), ⛔ aucune mise à jour locale
  //    optimiste — sinon l'écran montrerait une valeur que la base ⛔ n'a pas.
  const corriger = (posteId, champs) => garder(async () => {
    const r = await api.modifier(releve.id, posteId, champs);
    setReleve(r);
    setEnregistreLe(new Date().toISOString());
  });

  // 🔴 N°919 — l'énoncé de quantité est lu PAR LE SERVEUR (source unique du cœur).
  //    ⛔ Aucune interprétation côté téléphone. Rend {quantite, unite, comprise}.
  const dicterQuantite = async (texte) => {
    try { return await api.quantiteDictee(releve.id, texte); }
    catch (err) { signaler(err); return null; }
  };

  const consulter = (rid) => garder(async () => {
    if (releve && rid === releve.id) { setConsultation(null); return; }
    setConsultation(await api.lire(rid));
  });

  const clos = (r) => { setReleve(null); setProposition(null); setConsultation(r); rafraichirListe(); };
  // 🔴 N°1016 — ABANDONNER la visite en cours : confirmation explicite, état durable, historique
  //    conservé (la visite reste consultable dans la liste, mais sort de « Reprendre »).
  const abandonner = () => garder(async () => {
    await api.abandonner(releve.id);
    setAbandonDemande(false); setReleve(null); setProposition(null); setChoixApres(false);
    rafraichirListe();
  });

  if (chargement) return <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Chargement du relevé…</p>;

  const liste = <RelevesDuDossier releves={releves} actif={consultation?.id || releve?.id}
                                  onConsulter={consulter} />;
  // 🔴 P-MARIO-03 — ⛔ plus de « Code à transmettre » : l'application terrain s'ouvre avec la
  //    session de l'utilisateur (Nouvelle visite / Reprendre), ⛔ un code dicté au téléphone.

  if (consultation) {
    const vocabC = consultation.vocabulaire || {};
    const clos = consultation.etat === "clos";
    const detail = (
      <>
        {liste}
        <Encart ton="info" testId="releve-clos">
          {clos
            ? `${vocabC.releve || "Relevé"} clos le ${new Date(consultation.clos_le).toLocaleString("fr-FR")} — consultation seule.`
            : "Consultation du relevé."}
        </Encart>
        {/* É8 — la visite RESTITUÉE au dossier dit qui l'a faite et quand elle a été ouverte. */}
        <AuteurReleve releve={consultation} vocab={vocabC} />
        <ListePostes postes={consultation.postes} motPoste={vocabC.poste || "Poste"} />
        <NoteVisite api={api} releveId={consultation.id} lectureSeule />
        <PhotosReleve api={api} releveId={consultation.id} lectureSeule />
      </>
    );
    const boutons = (
      <>
        {actionsReleve && actionsReleve(consultation, { devis: devisCree })}
        {releve && (
          <Button variant="outline" data-testid="retour-releve-en-cours" onClick={() => setConsultation(null)}
                  style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}>
            Revenir au relevé en cours
          </Button>
        )}
        {!releve && peutOuvrir && (
          <Button data-testid="nouvelle-visite" onClick={nouvelleVisite} disabled={envoi}
                  style={{ background: compact ? "transparent" : "var(--oh-accent-solid)", color: compact ? "var(--oh-accent-text)" : "var(--oh-accent-contrast)",
                           border: compact ? "0.5px solid var(--oh-accent)" : undefined }}>
            Nouvelle visite
          </Button>
        )}
        {erreur && <Encart ton="error" testId="releve-erreur">{erreur}</Encart>}
      </>
    );
    // 🔴 N°1028 — MOBILE : « Visite terminée » = les travaux (✓ libellé — quantité unité) + le devis créé. Rien de technique :
    //    relevés du dossier, date/heure de clôture, auteur, note vide, photos 0 restent à la demande (bloc replié).
    if (compact) {
      return (
        <div data-testid="releve-consultation" style={{ display: "grid", gap: 12 }}>
          <p data-testid="visite-terminee" style={{ margin: 0, color: "var(--oh-text)", fontWeight: 500 }}>Visite terminée</p>
          <ul data-testid="travaux-enregistres" style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
            {(consultation.postes || []).map((p) => (
              <li key={p.id} style={{ color: "var(--oh-text)" }}>
                ✓ {p.designation}{p.quantite !== null && p.quantite !== undefined ? ` — ${nombre(p.quantite)}${p.unite ? ` ${p.unite}` : ""}` : ""}
              </li>
            ))}
          </ul>
          {boutons}
          <details data-testid="details-visite">
            <summary className="text-sm" style={{ color: "var(--oh-text-secondary)", cursor: "pointer" }}>Détails de la visite</summary>
            <div style={{ display: "grid", gap: 16, marginTop: 8 }}>{detail}</div>
          </details>
        </div>
      );
    }
    return (
      <div data-testid="releve-consultation" style={{ display: "grid", gap: 16 }}>
        {detail}
        {boutons}
      </div>
    );
  }

  if (!releve) {
    return (
      <div data-testid="releve-accueil" style={{ display: "grid", gap: 8 }}>
        {liste}
        {erreur && <Encart ton="error" testId="releve-erreur">{erreur}</Encart>}
        {peutOuvrir ? (
          <Button data-testid="nouvelle-visite" onClick={nouvelleVisite} disabled={envoi}
                  style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
            Nouvelle visite
          </Button>
        ) : (
          <Encart ton="info" testId="profil-absent">
            Aucun profil de relevé n'est déclaré pour cette entreprise : la visite ne peut pas être ouverte.
          </Encart>
        )}
      </div>
    );
  }

  const vocab = releve.vocabulaire || {};
  const motPoste = vocab.poste || "Poste";
  const ouvert = releve.etat !== "clos";
  const pretProposer = mode === "dictee" ? dictee.trim() : saisie.designation.trim();
  // 🔑 UNE SEULE instance de « Terminer la visite » dans l'écran, placée soit dans le
  //    choix d'après-confirmation, soit en bas. ⛔ Deux boutons portant le même mot
  //    rendraient l'écran ambigu pour l'humain (et le banc choisirait au hasard).
  const blocTerminer = <TerminerVisite api={api} releve={releve} motPoste={motPoste} onClos={clos} />;
  const blocAbandon = (
    <section data-testid="abandon-visite" style={{ display: "grid", gap: 8 }}>
      <Button variant="outline" data-testid="abandonner-visite" onClick={() => setAbandonDemande(true)} disabled={envoi}
              style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}>
        Abandonner la visite
      </Button>
      <AlertDialog open={abandonDemande} onOpenChange={setAbandonDemande}>
        <AlertDialogContent data-testid="abandon-dialogue"
                            style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text)",
                                     borderColor: "var(--oh-border)", borderRadius: 12 }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Abandonner cette visite ?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--oh-text-secondary)" }}>
              La visite ne sera plus proposée dans « Reprendre ». Elle reste dans l'historique du dossier,
              avec ses {motPoste.toLowerCase()}s, et aucun devis n'en sera créé. Rien n'est supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="annuler-abandon"
                               style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}>
              Garder la visite
            </AlertDialogCancel>
            <AlertDialogAction data-testid="confirmer-abandon" disabled={envoi} onClick={abandonner}
                               style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
              Abandonner la visite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );

  // 🔴 N°1025 — ÉCRAN MOBILE COMPACT (`compact`, visite EN COURS) : « Visite en cours » puis l'ACTION (dicter les
  //    travaux) ; la structure interne (liste des relevés, titre, auteur/date, messages de reprise et de sauvegarde)
  //    passe derrière « Détails ». ⛔ Rien n'est supprimé, ⛔ aucune logique de travaux modifiée.
  const creerLeDevis = async () => {
    setErreurFinale(""); setFinalisation(true);
    try {
      const devis = await finaliser(releve);                        // clôture + devis, par l'HÔTE (⛔ ce module n'importe pas metier_hub)
      setReleve(null); setChoixApres(false); setProposition(null);
      setDevisCree(devis);
      setConsultation(await api.lire(releve.id)); rafraichirListe();
    } catch (err) {
      setErreurFinale(err.message || "Le devis n'a pas pu être créé.");
      try { setReleve(await api.releveEnCours()); } catch { /* conservé tel quel */ }   // la visite reste OUVERTE pour corriger
    } finally { setFinalisation(false); }
  };
  const mobile = compact && ouvert;
  const apresEnregistrement = mobile && choixApres && !proposition;
  const enteteDetail = (
    <>
      {liste}
      <div style={{ color: "var(--oh-text)", fontWeight: 500 }}>
        {vocab.releve || "Relevé"} — {vocab.session || "Visite"}
      </div>
      <AuteurReleve releve={releve} vocab={vocab} />
      {reprise && (
        <Encart ton="info" testId="reprise">
          {vocab.session || "Visite"} en cours reprise : ce qui a déjà été enregistré est conservé.
        </Encart>
      )}
    </>
  );
  const ordreAction = mobile ? { order: -2 } : {};

  return (
    <div data-testid="releve-chantier" style={{ display: "grid", gap: 16 }}>
      {!mobile && enteteDetail}
      {erreur && <Encart ton="error" testId="releve-erreur">{erreur}</Encart>}
      {/* 🔴 P1 — une photo ne se perd pas en silence : l'état d'envoi vit ICI (jamais démonté avec le menu ☰), et la prise de photo est accessible d'ici. */}
      <PhotosNonEnvoyees releveId={releve.id} />
      {/* Mobile : la phrase de sauvegarde n'apparaît qu'APRÈS un enregistrement (« Enregistré à HH:MM »). */}
      {!mobile && (
      <p data-testid="etat-sauvegarde" className="text-sm" style={{ color: "var(--oh-text-secondary)", ...ordreAction }}>
        {/* 🔴 N°915-D — après confirmation, DIRE que la saisie suivante est ouverte : la
            zone de dictée revient d'elle-même, mais rien ⛔ ne le disait. */}
        {enregistreLe
          ? `Enregistré sur le serveur à ${new Date(enregistreLe).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`
            + (ouvert ? ` — vous pouvez enchaîner le ${motPoste.toLowerCase()} suivant.` : "")
          : "Chaque travail confirmé est enregistré sur le serveur."}
      </p>
      )}

      {!apresEnregistrement && (ouvert
        ? <GrillePostes postes={releve.postes} motPoste={motPoste} envoi={envoi} onRetirer={retirer}
                        onCorriger={corriger} unitesAutorisees={releve.unites_autorisees}
                        chargerProduits={api.catalogue && profilCode ? () => api.catalogue(releve.profil_code || profilCode) : undefined} />
        : <ListePostes postes={releve.postes} motPoste={motPoste} />)}

      {mobile && !choixApres && !proposition && finaliser && releve.postes.length > 0 && (
        <section data-testid="devis-visite-reprise" style={{ display: "grid", gap: 8 }}>
          <ZonePhotosAvantDevis api={api} releveId={releve.id} ouvert={ouvert} />
          <Button data-testid="creer-devis-final" onClick={creerLeDevis} disabled={envoi || finalisation}
                  style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
            {finalisation ? "Création du devis…" : "Créer le devis"}
          </Button>
          {erreurFinale && <Encart ton="error" testId="devis-final-erreur">{erreurFinale}</Encart>}
        </section>
      )}

      {apresEnregistrement && (
        <section data-testid="apres-enregistrement" style={{ display: "grid", gap: 12, order: -2 }}>
          <ul data-testid="travaux-enregistres" style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
            {releve.postes.map((p) => (
              <li key={p.id} data-testid={`ligne-travail-${p.id}`} style={{ color: "var(--oh-text)" }}>
                ✓ {p.designation}{p.quantite !== null && p.quantite !== undefined ? ` — ${nombre(p.quantite)}${p.unite ? ` ${p.unite}` : ""}` : ""}
              </li>
            ))}
          </ul>
          <Button data-testid="nouveau-poste" onClick={() => setChoixApres(false)} disabled={envoi || finalisation}
                  aria-label={`Dicter un ${motPoste.toLowerCase()}`}
                  style={{ background: "transparent", color: "var(--oh-accent-text)", border: "0.5px solid var(--oh-accent)" }}>
            {/* 🔴 N°1029 — l'action attendue est de PARLER : « Dicter » en évidence (graisse 500 = titres/labels de la charte). */}
            🎙 <strong data-testid="mot-dicter" style={{ fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}>Dicter</strong> un {motPoste.toLowerCase()}
          </Button>
          {/* 🔴 Les photos se prennent AVANT « Créer le devis » : celui-ci clôt la visite, et le serveur refuse ensuite toute photo. */}
          <ZonePhotosAvantDevis api={api} releveId={releve.id} ouvert={ouvert} rappel={!!finaliser} />
          {finaliser && (
            <Button data-testid="creer-devis-final" onClick={creerLeDevis} disabled={envoi || finalisation}
                    style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
              {finalisation ? "Création du devis…" : "Créer le devis"}
            </Button>
          )}
          {erreurFinale && <Encart ton="error" testId="devis-final-erreur">{erreurFinale}</Encart>}
          {/* Détails, correction, suppression, recherche : DERRIÈRE un bloc replié (rien n'est supprimé). */}
          <details data-testid="details-travaux">
            <summary className="text-sm" style={{ color: "var(--oh-text-secondary)", cursor: "pointer" }}>Détails</summary>
            <div style={{ marginTop: 8 }}>
              <GrillePostes postes={releve.postes} motPoste={motPoste} envoi={envoi} onRetirer={retirer}
                            onCorriger={corriger} unitesAutorisees={releve.unites_autorisees}
                            chargerProduits={api.catalogue && profilCode ? () => api.catalogue(releve.profil_code || profilCode) : undefined} />
            </div>
          </details>
        </section>
      )}

      {ouvert && !proposition && choixApres && !apresEnregistrement && (
        <section data-testid="choix-apres-poste" aria-label="Poste enregistré — et maintenant ?"
                 style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-accent)",
                          borderRadius: 12, padding: 16, display: "grid", gap: 12, ...ordreAction }}>
          <Encart ton="info" testId="poste-enregistre-confirmation">
            {motPoste} enregistré. Que voulez-vous faire maintenant ?
          </Encart>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button data-testid="nouveau-poste" onClick={() => setChoixApres(false)} disabled={envoi}
                    style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
              Nouveau {motPoste.toLowerCase()}
            </Button>
          </div>
          {/* ⛔ Aucun second mécanisme de clôture : c'est LE composant Core existant. */}
          {blocTerminer}
        </section>
      )}

      {ouvert && !proposition && !choixApres && (
        <section data-testid="saisie-poste" style={{ display: "grid", gap: 8, ...ordreAction }}>
          {/* 🔴 MARIO P1 UX — le MODE de saisie est un SÉLECTEUR d'état (un seul actif, annoncé), nommé « Mode de saisie » ;
              ⛔ ce n'est pas une action : les ACTIONS (🎙 Dicter un travail, Proposer…) restent des boutons à verbe, ailleurs. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid="mode-saisie-selecteur">
            <span style={{ fontSize: 12, color: "var(--oh-text-muted)" }}>Mode de saisie</span>
            <div role="tablist" aria-label="Mode de saisie"
                 style={{ display: "inline-flex", padding: 2, borderRadius: 8, background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)" }}>
              {[["dictee", "mode-dictee", "Dictée"], ["saisie", "mode-saisie", "Saisie libre"]].map(([valeur, testId, libelle]) => {
                const actif = mode === valeur;
                return (
                  <button key={valeur} type="button" role="tab" aria-selected={actif} data-testid={testId} onClick={() => setMode(valeur)}
                          style={{ height: 32, padding: "0 12px", borderRadius: 6, border: "none", cursor: "pointer",
                                   fontWeight: actif ? 500 : 400, fontSize: 14,
                                   background: actif ? "var(--oh-accent-bg)" : "transparent",
                                   color: actif ? "var(--oh-accent-text)" : "var(--oh-text-secondary)" }}>
                    {libelle}
                  </button>
                );
              })}
            </div>
          </div>
          {mode === "dictee" ? (
            <>
              <textarea data-testid="dictee-texte" value={dictee} rows={3}
                        placeholder={`Décrivez le ${motPoste.toLowerCase()} (ex. « peinture murs 10 mètres par 2,50 »)`}
                        onChange={(ev) => setDictee(ev.target.value)}
                        style={{ borderRadius: 8, border: "0.5px solid var(--oh-border)", padding: 8,
                                 background: "var(--oh-bg)", color: "var(--oh-text)" }} />
              {/* 🔴 N°963 — PRINCIPAL : enregistrement audio → transcription serveur.
                   SpeechRecognition ⛔ n'est QU'UN repli, affiché si l'audio manque. */}
              {audioDispo ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {!enregistre ? (
                    <Button variant="outline" data-testid="dictee-audio-start" onClick={demarrerAudio}
                            disabled={transcription}
                            style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent" }}>
                      {transcription ? "Transcription…" : (mobile ? `Dicter un ${motPoste.toLowerCase()}` : "Dicter au micro")}
                    </Button>
                  ) : (
                    <Button variant="outline" data-testid="dictee-audio-stop" onClick={arreterAudio}
                            style={{ borderColor: "var(--oh-error-text)", color: "var(--oh-error-text)", background: "var(--oh-error-bg)" }}>
                      ● Arrêter et transcrire
                    </Button>
                  )}
                  {enregistre && (
                    <span className="text-sm" data-testid="dictee-audio-etat" style={{ color: "var(--oh-error-text)" }}>
                      Enregistrement en cours…
                    </span>
                  )}
                  {transcription && (
                    <span className="text-sm" data-testid="dictee-audio-transcrit" style={{ color: "var(--oh-text-secondary)" }}>
                      Transcription…
                    </span>
                  )}
                </div>
              ) : micro ? (
                <Button variant="outline" data-testid="dictee-micro" onClick={demarrer} disabled={ecoute}
                        style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent" }}>
                  {ecoute ? "J'écoute…" : (mobile ? `Dicter un ${motPoste.toLowerCase()}` : "Dicter au micro")}
                </Button>
              ) : null}
              {erreurMicro && <p className="text-sm" style={{ color: "var(--oh-error-text)" }}>{erreurMicro}</p>}
            </>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              <input data-testid="saisie-designation" placeholder="Désignation libre" value={saisie.designation}
                     onChange={(ev) => setSaisie({ ...saisie, designation: ev.target.value })}
                     style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)", padding: "0 8px",
                              background: "var(--oh-bg)", color: "var(--oh-text)" }} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <input data-testid="saisie-quantite" placeholder="Quantité" inputMode="decimal" value={saisie.quantite}
                       onChange={(ev) => setSaisie({ ...saisie, quantite: ev.target.value })}
                       style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)", padding: "0 8px",
                                background: "var(--oh-bg)", color: "var(--oh-text)" }} />
                <select data-testid="saisie-unite" aria-label="Unité" value={saisie.unite}
                        onChange={(ev) => setSaisie({ ...saisie, unite: ev.target.value })}
                        style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)", padding: "0 8px",
                                 background: "var(--oh-bg)", color: "var(--oh-text)" }}>
                  <option value="">sans unité</option>
                  {(releve.unites_autorisees || []).map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
          )}
          {/* 🔴 N°915-D — « Proposer » ⛔ N'ENREGISTRE PAS : c'est l'étape 1 sur 2. Mario a
              cru le travail créé après ce bouton (N°914-C). ⛔ Pas de second mécanisme de
              création : on NOMME l'étape au lieu d'en ajouter une. */}
          <Button data-testid="proposer-poste" onClick={proposer} disabled={!pretProposer || envoi}
                  style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
            Proposer le {motPoste.toLowerCase()}
          </Button>
          {!mobile && (
            <p data-testid="etape-proposer" className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
              Étape 1 sur 2 — ceci ⛔ n'enregistre pas encore : vous relirez la désignation, la
              quantité et l'unité, puis vous confirmerez.
            </p>
          )}
        </section>
      )}

      {ouvert && proposition && (
        <div style={ordreAction}>
          <Proposition key={JSON.stringify(proposition)} proposition={proposition}
                       unitesAutorisees={releve.unites_autorisees} motPoste={motPoste}
                       onConfirmer={confirmer} onAbandonner={() => setProposition(null)} envoi={envoi}
                       onDicterQuantite={dicterQuantite} compact={mobile} />
        </div>
      )}

      {/* 🔴 N°1025 — mobile : note, photos, clôture, abandon et informations internes sont dans le MENU ☰ (à la demande). */}
      {!mobile && (
        <>
          <NoteVisite api={api} releveId={releve.id} lectureSeule={!ouvert} />
          <PhotosReleve api={api} releveId={releve.id} postes={releve.postes} motPoste={motPoste} lectureSeule={!ouvert} />
          {ouvert && !proposition && !choixApres && blocTerminer}
          {ouvert && !proposition && !choixApres && !apiFourni && blocAbandon}
        </>
      )}
      {mobile && menuOuvert && (
        <div data-testid="menu-contenu-visite" style={{ order: -4, display: "grid", gap: 12, ...CARTE_MENU }}>
          <div data-testid="visite-etat" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
            {vocab.session || "Visite"} en cours
          </div>
          {enteteDetail}
          <p data-testid="etat-sauvegarde-detail" className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
            Chaque {motPoste.toLowerCase()} confirmé est enregistré sur le serveur.
          </p>
          <NoteVisite api={api} releveId={releve.id} lectureSeule={!ouvert} />
          <PhotosReleve api={api} releveId={releve.id} postes={releve.postes} motPoste={motPoste} lectureSeule={!ouvert} />
          {!proposition && !choixApres && blocTerminer}
          {!proposition && !choixApres && !apiFourni && blocAbandon}
        </div>
      )}
    </div>
  );
}

export default ReleveChantier;
